// Fit: render children at a fixed design size (w x h) and scale them down uniformly to fit the box Fit gets from its
// parent (default flex: 1 1 auto; min-height: 0). For hero pieces (reward card, flame block, share-card preview) that
// must keep their proportions on short or narrow phones. Never scales up.
//   <Fit w={300} h={344}><XCard fit="none" w={300} h={344} ... /></Fit>
import type { ComponentChildren, JSX } from 'preact';
import { useRef } from 'preact/hooks';
import { useSize } from '../lib/useSize';

export interface FitProps {
  w: number;
  h: number;
  children: ComponentChildren;
  /** Minimum scale (default 0.5); below it the content overflows instead of shrinking further. */
  min?: number;
  /** Where to place the scaled box (default center). */
  align?: 'center' | 'start' | 'end';
  class?: string;
  style?: JSX.CSSProperties;
}

export function Fit({ w, h, children, min = 0.5, align = 'center', class: cls, style }: FitProps) {
  const box = useRef<HTMLDivElement>(null);
  const size = useSize(box, { w, h });
  const k = Math.max(min, Math.min(1, size.w / w || 1, size.h / h || 1));
  return (
    <div ref={box} class={cls} style={{ flex: '1 1 auto', minHeight: 0, width: '100%', display: 'flex', alignItems: align === 'start' ? 'flex-start' : align === 'end' ? 'flex-end' : 'center', justifyContent: 'center', ...style }}>
      <div style={{ width: Math.round(w * k), height: Math.round(h * k), position: 'relative', flex: 'none' }}>
        <div style={{ position: 'absolute', left: 0, top: 0, width: w, height: h, transform: k === 1 ? undefined : `scale(${k})`, transformOrigin: '0 0' }}>
          {children}
        </div>
      </div>
    </div>
  );
}
